import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { partnerConferencesAPI, sessionsAPI, taxonomyAPI, preferencesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ProjectStatusCalendar } from '../components/private-deliveries/ProjectStatusCalendar';
import { sessionTrainers } from '../components/shared/calendar/calendarUtils';
import { ColumnsPicker } from '../components/shared/ColumnsPicker';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit2,
  Trash2,
  Copy,
  MapPin,
  Calendar,
  Users,
  Filter,
  Globe,
  Star,
  CalendarRange,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_VARIANTS = {
  requested: { label: 'Requested', className: 'bg-yellow-100 text-yellow-800' },
  planning: { label: 'Planning', className: 'bg-slate-100 text-slate-800' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', className: 'bg-violet-100 text-violet-800' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
};

const REGION_COLORS = {
  Asia: 'bg-orange-100 text-orange-800',
  Americas: 'bg-blue-100 text-blue-800',
  Europe: 'bg-purple-100 text-purple-800',
  MEA: 'bg-teal-100 text-teal-800',
  Other: 'bg-gray-100 text-gray-700',
};

export const PartnerConferencesPage = () => {
  const { user, isPM } = useAuth();
  const navigate = useNavigate();
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('events');
  const [calendarSessions, setCalendarSessions] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [visibleCustomCols, setVisibleCustomCols] = useState([]);
  const [confPage, setConfPage] = useState(1);
  const [colFilters, setColFilters] = useState({ region: new Set(), status: new Set() });
  const setColFilter = (key, s) => setColFilters(prev => ({ ...prev, [key]: s }));
  const customColumns = categories.map(c => ({ key: `custom:${c.id}`, taxonomyKey: c.id, label: c.label }));

  useEffect(() => {
    taxonomyAPI.getAll().then(setCategories).catch(() => {});
    preferencesAPI.get().then(prefs => {
      if (Array.isArray(prefs.partner_conferences_columns)) setVisibleCustomCols(prefs.partner_conferences_columns);
    }).catch(() => {});
  }, []);
  const toggleCustomCol = (key) => {
    setVisibleCustomCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      preferencesAPI.set('partner_conferences_columns', next).catch(() => {});
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (isPM() && user?.id) filters.pm_id = user.id;
      const data = await partnerConferencesAPI.getAll(filters);
      setConferences(data);
    } catch {
      toast.error('Failed to load conferences');
    } finally {
      setLoading(false);
    }
  }, [isPM, user]);

  useEffect(() => { load(); }, [load]);

  const loadCalendarSessions = useCallback(async () => {
    try {
      setCalendarLoading(true);
      const allSessions = await sessionsAPI.getAll();
      const conferenceIds = new Set(conferences.map(c => c.id));
      setCalendarSessions(allSessions.filter(s => conferenceIds.has(s.training_project_id)));
      setCalendarLoaded(true);
    } catch {
      toast.error('Failed to load calendar sessions');
    } finally {
      setCalendarLoading(false);
    }
  }, [conferences]);

  useEffect(() => {
    if (activeTab === 'calendar' && !calendarLoaded) loadCalendarSessions();
  }, [activeTab, calendarLoaded, loadCalendarSessions]);

  const conferenceStatusMap = React.useMemo(
    () => Object.fromEntries(conferences.map(c => [c.id, c.status])),
    [conferences]
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this conference event?')) return;
    try {
      await partnerConferencesAPI.delete(id);
      toast.success('Conference deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleCopy = async (id) => {
    try {
      const copied = await partnerConferencesAPI.copy(id, user?.id);
      toast.success('Conference copied successfully');
      load();
      navigate(`/partner-conferences/${copied.id}`);
    } catch {
      toast.error('Failed to copy conference');
    }
  };

  const filtered = conferences.filter(c => {
    if (search) {
      const s = search.toLowerCase();
      if (!(c.title?.toLowerCase().includes(s) || c.city?.toLowerCase().includes(s) || c.country?.toLowerCase().includes(s))) return false;
    }
    if (colFilters.region.size && !colFilters.region.has(c.region)) return false;
    if (colFilters.status.size && !colFilters.status.has(c.status)) return false;
    return true;
  });

  const regionOpts = [...new Set(conferences.map(c => c.region).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const statusOpts = Object.entries(STATUS_VARIANTS).filter(([k]) => conferences.some(c => c.status === k)).map(([k, { label }]) => ({ value: k, label }));

  useEffect(() => { setConfPage(1); }, [search, colFilters]);
  const pagedConf = filtered.slice((confPage - 1) * PAGE_SIZE, confPage * PAGE_SIZE);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(new Date(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="partner-conferences-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Partner Conferences</h1>
          <p className="text-muted-foreground">Manage AI Tour for Partners and large-scale conference events</p>
        </div>
        <Button
          onClick={() => navigate('/partner-conferences/new')}
          data-testid="new-conference-btn"
          className="bg-lime text-lime-foreground hover:bg-lime/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Conference Event
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, city or country..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10"
                data-testid="conference-search"
              />
            </div>
            {customColumns.length > 0 && (
              <ColumnsPicker columns={customColumns} visibleKeys={visibleCustomCols} onToggle={toggleCustomCol} testId="pc-columns" />
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <CalendarRange className="w-4 h-4 mr-1.5" />
            Calendar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="mt-4">
      <ActiveFiltersBar
        filters={[
          { key: 'region', label: 'Region', set: colFilters.region, options: regionOpts, onChange: (s) => setColFilter('region', s) },
          { key: 'status', label: 'Status', set: colFilters.status, options: statusOpts, onChange: (s) => setColFilter('status', s) },
        ]}
        search={search}
        onClearSearch={() => setSearch('')}
        testId="pc-active-filters"
      />
      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded" />)}
            </div>
          ) : filtered.length > 0 ? (
            <Table containerClassName="max-h-[calc(100vh-330px)]">
              <TableHeader sticky>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead><ColumnFilterPopover label="Region" options={regionOpts} selected={colFilters.region} onChange={(s) => setColFilter('region', s)} testId="pc-col-filter-region" /></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Attendance</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead><ColumnFilterPopover label="Status" options={statusOpts} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} testId="pc-col-filter-status" /></TableHead>
                  {customColumns.filter(c => visibleCustomCols.includes(c.key)).map(c => (
                    <TableHead key={c.key}>{c.label}</TableHead>
                  ))}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedConf.map(c => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer hover:bg-muted/50"
                    data-testid={`conference-row-${c.id}`}
                    onClick={() => navigate(`/partner-conferences/${c.id}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-lime flex-shrink-0" />
                        <span>{c.title}</span>
                      </div>
                      {c.workload && <p className="text-xs text-muted-foreground mt-0.5 ml-6">{c.workload}</p>}
                    </TableCell>
                    <TableCell>
                      {c.city || c.country ? (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm">{[c.city, c.country].filter(Boolean).join(', ')}</span>
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {c.region ? (
                        <Badge className={REGION_COLORS[c.region] || REGION_COLORS.Other}>
                          {c.region}
                        </Badge>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm">{formatDate(c.preferred_dates)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {c.total_attendance != null ? (
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium">{c.total_attendance.toLocaleString()}</span>
                        </div>
                      ) : <span className="text-muted-foreground text-sm">—</span>}
                    </TableCell>
                    <TableCell>
                      {c.conference_feedback_score != null ? (
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                          <span className="text-sm font-medium">{c.conference_feedback_score}/5</span>
                        </div>
                      ) : <span className="text-muted-foreground text-sm">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_VARIANTS[c.status]?.className || ''}>
                        {STATUS_VARIANTS[c.status]?.label || c.status}
                      </Badge>
                    </TableCell>
                    {customColumns.filter(col => visibleCustomCols.includes(col.key)).map(col => (
                      <TableCell key={col.key} className="text-sm">{c.custom_fields?.[col.taxonomyKey] || '—'}</TableCell>
                    ))}
                    <TableCell onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/partner-conferences/${c.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/partner-conferences/${c.id}/edit`)}>
                            <Edit2 className="w-4 h-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCopy(c.id)} data-testid={`conf-copy-${c.id}`}>
                            <Copy className="w-4 h-4 mr-2" />Copy Event
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No conference events found</h3>
              <p className="text-muted-foreground mb-4">
                {search || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first Partner Conference event to get started'}
              </p>
              {!search && statusFilter === 'all' && (
                <Button onClick={() => navigate('/partner-conferences/new')} className="bg-lime text-lime-foreground hover:bg-lime/90">
                  <Plus className="w-4 h-4 mr-2" />New Conference Event
                </Button>
              )}
            </div>
          )}
          <TablePagination page={confPage} total={filtered.length} onPageChange={setConfPage} testId="conf-pagination" itemLabel="events" />
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4" data-testid="partner-conferences-calendar-tab">
          {calendarLoading ? (
            <div className="h-96 bg-muted animate-pulse rounded-xl" />
          ) : (
            <ProjectStatusCalendar
              items={calendarSessions.map(s => ({
                id: s.id,
                date: s.session_date,
                endDate: s.end_date,
                startTime: s.start_time,
                endTime: s.end_time,
                trainers: sessionTrainers(s),
                title: s.title,
                statusKey: conferenceStatusMap[s.training_project_id] || 'requested',
                training_project_id: s.training_project_id,
              }))}
              onItemClick={(it) => navigate(`/partner-conferences/${it.training_project_id}`)}
              testId="pc-calendar-view"
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnerConferencesPage;
